import type { ApiMessage } from '../../types/messages';
import type { OpenImRawMessage } from './types';

/**
 * Mirrors Android `TgImOpenImGroupNotificationMapper` (1501–1520 → TG service / fallback text).
 * Web maps to `ApiMessage.content.action` so Telegram Web renders centered service bubbles
 * (same UX as native WebA ActionMessage).
 */

const CT_GROUP_CREATED = 1501;
const CT_GROUP_INFO_SET = 1502;
const CT_JOIN_APPLICATION = 1503;
const CT_MEMBER_QUIT = 1504;
const CT_APPLICATION_ACCEPTED = 1505;
const CT_APPLICATION_REJECTED = 1506;
const CT_OWNER_TRANSFERRED = 1507;
const CT_MEMBER_KICKED = 1508;
const CT_MEMBER_INVITED = 1509;
const CT_MEMBER_ENTER = 1510;
const CT_GROUP_DISMISSED = 1511;
const CT_MEMBER_MUTED = 1512;
const CT_MEMBER_UNMUTED = 1513;
const CT_GROUP_MUTED = 1514;
const CT_GROUP_UNMUTED = 1515;
const CT_MEMBER_INFO_SET = 1516;
const CT_MEMBER_TO_ADMIN = 1517;
const CT_MEMBER_TO_ORDINARY = 1518;
const CT_GROUP_ANNOUNCEMENT = 1519;
const CT_GROUP_NAME_CHANGED = 1520;

type UserLike = Record<string, unknown>;

function cleanText(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function tryParseJsonObject(raw: string): Record<string, unknown> | undefined {
  const trimmed = raw.trim();
  if (!trimmed.startsWith('{')) return undefined;
  try {
    const parsed = JSON.parse(trimmed) as unknown;
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
  } catch {
    // ignore
  }
  return undefined;
}

function asUser(value: unknown): UserLike | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  return value as UserLike;
}

function firstUser(payload: Record<string, unknown>, ...keys: string[]): UserLike | undefined {
  for (const key of keys) {
    const user = asUser(payload[key]);
    if (user) return user;
  }
  return undefined;
}

function displayName(user?: UserLike): string {
  if (!user) return '';
  return cleanText(user.nickname)
    || cleanText(user.nickName)
    || cleanText(user.remark)
    || cleanText(user.userID)
    || cleanText(user.userId)
    || '';
}

function userId(user?: UserLike): string {
  if (!user) return '';
  return cleanText(user.userID) || cleanText(user.userId) || '';
}

function groupNameFrom(payload: Record<string, unknown>): string {
  const group = asUser(payload.group);
  return cleanText(group?.groupName)
    || cleanText(group?.name)
    || cleanText(payload.groupName)
    || cleanText(payload.name)
    || '';
}

function announcementText(payload: Record<string, unknown>): string {
  const group = asUser(payload.group);
  return cleanText(payload.notification)
    || cleanText(payload.announcement)
    || cleanText(group?.notification)
    || cleanText(group?.announcement)
    || '';
}

function unwrapNotificationPayload(raw: OpenImRawMessage): Record<string, unknown> | undefined {
  const notif = (raw as { notificationElem?: { detail?: unknown } }).notificationElem;
  if (notif?.detail) {
    if (typeof notif.detail === 'string') {
      const parsed = tryParseJsonObject(notif.detail);
      if (parsed) return parsed;
    } else if (typeof notif.detail === 'object' && !Array.isArray(notif.detail)) {
      return notif.detail as Record<string, unknown>;
    }
  }

  const content = raw.content;
  if (content && typeof content === 'object' && !Array.isArray(content)) {
    return content as Record<string, unknown>;
  }
  if (typeof content === 'string' && content) {
    return tryParseJsonObject(content);
  }
  return undefined;
}

function resolveTargetUser(payload: Record<string, unknown>, contentType: number): UserLike | undefined {
  if (contentType === CT_MEMBER_QUIT) {
    return firstUser(payload, 'quitUser', 'opUser', 'operatorUser');
  }
  return firstUser(payload, 'kickedUser', 'invitedUser', 'entrantUser', 'mutedUser',
    'changedUser', 'newOwner', 'member', 'user', 'targetUser');
}

function resolveActorUser(payload: Record<string, unknown>, contentType: number): UserLike | undefined {
  if (contentType === CT_MEMBER_QUIT) {
    return firstUser(payload, 'quitUser', 'opUser', 'operatorUser');
  }
  return firstUser(payload, 'opUser', 'operatorUser', 'inviterUser', 'ownerUser');
}

function collectInvitedUserIds(payload: Record<string, unknown>, fallback?: UserLike): string[] {
  const ids: string[] = [];
  const invited = payload.invitedUserList ?? payload.memberList ?? payload.users;
  if (Array.isArray(invited)) {
    invited.forEach((item) => {
      const id = userId(asUser(item));
      if (id) ids.push(id);
    });
  }
  if (ids.length) return ids;
  const single = userId(resolveTargetUser(payload, CT_MEMBER_INVITED)) || userId(fallback);
  if (single) ids.push(single);
  return ids;
}

function humanSummary(
  contentType: number,
  actor?: UserLike,
  groupName?: string,
  target?: UserLike,
  payload?: Record<string, unknown>,
): string {
  const actorName = displayName(actor) || '某人';
  const targetName = displayName(target) || '某人';
  const title = groupName || '';

  switch (contentType) {
    case CT_GROUP_CREATED:
      return `${actorName} 创建了群组${title ? `「${title}」` : ''}`;
    case CT_GROUP_INFO_SET:
      return `${actorName} 更新了群资料`;
    case CT_JOIN_APPLICATION:
      return `${actorName} 申请加入群组`;
    case CT_APPLICATION_ACCEPTED:
      return '入群申请已通过';
    case CT_APPLICATION_REJECTED:
      return '入群申请已拒绝';
    case CT_OWNER_TRANSFERRED:
      return `${actorName} 将群主转让给 ${targetName}`;
    case CT_MEMBER_INVITED:
      return `${actorName} 邀请 ${targetName} 加入了群组`;
    case CT_MEMBER_ENTER:
      return `${targetName || actorName} 加入了群组`;
    case CT_MEMBER_KICKED:
      return `${actorName} 将 ${targetName} 移出了群组`;
    case CT_MEMBER_QUIT:
      return `${actorName} 退出了群组`;
    case CT_GROUP_NAME_CHANGED:
      return title
        ? `${actorName} 将群名修改为「${title}」`
        : `${actorName} 修改了群名称`;
    case CT_GROUP_DISMISSED:
      return '群组已解散';
    case CT_MEMBER_MUTED:
      return `${actorName} 禁言了 ${targetName}`;
    case CT_MEMBER_UNMUTED:
      return `${actorName} 取消了 ${targetName} 的禁言`;
    case CT_GROUP_MUTED:
      return `${actorName} 开启了全员禁言`;
    case CT_GROUP_UNMUTED:
      return `${actorName} 关闭了全员禁言`;
    case CT_MEMBER_TO_ADMIN:
      return `${actorName} 将 ${targetName} 设为管理员`;
    case CT_MEMBER_TO_ORDINARY:
      return `${actorName} 取消了 ${targetName} 的管理员`;
    case CT_MEMBER_INFO_SET:
      return `${actorName} 更新了成员资料`;
    case CT_GROUP_ANNOUNCEMENT: {
      const ann = payload ? announcementText(payload) : '';
      return ann ? `群公告：${ann}` : `${actorName} 更新了群公告`;
    }
    default:
      return '群通知';
  }
}

export function isOpenImGroupNotification(raw: OpenImRawMessage): boolean {
  const contentType = Number(raw.contentType) || 0;
  return contentType >= 1500 && contentType < 1600;
}

/**
 * Returns ApiMessage content for a group system notification, or undefined if unusable.
 * Prefer native Telegram action types so WebA ActionMessage renders like stock Telegram Web.
 */
export function mapOpenImGroupNotificationContent(
  raw: OpenImRawMessage,
): ApiMessage['content'] | undefined {
  if (!isOpenImGroupNotification(raw)) return undefined;

  const contentType = Number(raw.contentType) || 0;
  const payload = unwrapNotificationPayload(raw) || {};
  const actor = resolveActorUser(payload, contentType);
  const target = resolveTargetUser(payload, contentType);
  const groupName = groupNameFrom(payload);
  const actorId = userId(actor);
  const targetId = userId(target);

  switch (contentType) {
    case CT_GROUP_CREATED:
      return {
        action: {
          mediaType: 'action',
          type: 'chatCreate',
          title: groupName || '群组',
          userIds: actorId ? [actorId] : [],
        },
      };
    case CT_MEMBER_INVITED:
    case CT_MEMBER_ENTER: {
      const userIds = collectInvitedUserIds(payload, target || actor);
      return {
        action: {
          mediaType: 'action',
          type: 'chatAddUser',
          userIds: userIds.length ? userIds : (actorId ? [actorId] : []),
        },
      };
    }
    case CT_MEMBER_KICKED:
      return {
        action: {
          mediaType: 'action',
          type: 'chatDeleteUser',
          userId: targetId || actorId || '0',
        },
      };
    case CT_MEMBER_QUIT:
      return {
        action: {
          mediaType: 'action',
          type: 'chatDeleteUser',
          userId: actorId || targetId || '0',
        },
      };
    case CT_GROUP_NAME_CHANGED:
      return {
        action: {
          mediaType: 'action',
          type: 'chatEditTitle',
          title: groupName || cleanText(payload.groupName) || cleanText(payload.name) || '群组',
        },
      };
    default: {
      const summary = humanSummary(contentType, actor, groupName, target, payload);
      if (!summary) return undefined;
      return {
        action: {
          mediaType: 'action',
          type: 'customAction',
          message: summary,
        },
      };
    }
  }
}
